import { useTranslator } from '@Localization';
import { useInjection } from 'inversify-react';
import { IUserStore, Plan, UserRole } from '../../__legacy__/modules/user/user.store';
import { WorkspaceRole } from '../../__legacy__/modules/workspaces/workspaces.store';
import { checkAll, IPermissionCheckResult } from '../permissions/permissionsHelpers';
import { ICurrentWorkspaceStore } from '../workspaces/currentWorkspace/CurrentWorkspace.store';

interface IJobPermissions {
  canList: () => IPermissionCheckResult;
  canReadCostList: (isOwner: boolean) => IPermissionCheckResult;
  canRun: () => IPermissionCheckResult;
  canUpdate: (isOwner: boolean) => IPermissionCheckResult;
  canDelete: (isOwner: boolean) => IPermissionCheckResult;
  canImport: () => IPermissionCheckResult;
  canDownload: (isOwner: boolean) => IPermissionCheckResult;
}

export const useJobPermissions = (): IJobPermissions => {
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);
  const userStore = useInjection(IUserStore.$);
  const currentWorkspace = currentWorkspaceStore.currentWorkspace;
  const { t } = useTranslator();

  const canList = (): IPermissionCheckResult => {
    return checkAll(
      [Plan.Admin, Plan.Training, Plan.Inactive],
      [WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Trainer, WorkspaceRole.Developer, WorkspaceRole.Collaborator, WorkspaceRole.Guest],
      currentWorkspace,
      t
    );
  };

  const canReadCostList = (isOwner: boolean): IPermissionCheckResult => {
    if (isOwner) {
      return checkAll([Plan.Admin, Plan.Training, Plan.Inactive], [WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Trainer], currentWorkspace, t);
    } else {
      return checkAll([Plan.Admin, Plan.Training, Plan.Inactive], [WorkspaceRole.Owner, WorkspaceRole.Manager], currentWorkspace, t);
    }
  };

  const canRun = (): IPermissionCheckResult => {
    return checkAll([Plan.Admin, Plan.Training], [WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Trainer], currentWorkspace, t);
  };

  const canUpdate = (isOwner: boolean): IPermissionCheckResult => {
    if (isOwner) {
      return checkAll([Plan.Admin, Plan.Training, Plan.Inactive], [WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Trainer], currentWorkspace, t);
    } else {
      return checkAll([Plan.Admin, Plan.Training, Plan.Inactive], [WorkspaceRole.Owner, WorkspaceRole.Manager], currentWorkspace, t);
    }
  };

  const canDelete = (isOwner: boolean): IPermissionCheckResult => {
    if (isOwner) {
      return checkAll([Plan.Admin, Plan.Training, Plan.Inactive], [WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Trainer], currentWorkspace, t);
    } else {
      return checkAll([Plan.Admin, Plan.Training, Plan.Inactive], [WorkspaceRole.Owner, WorkspaceRole.Manager], currentWorkspace, t);
    }
  };

  const canImport = (): IPermissionCheckResult => {
    return { allowed: userStore.userInfo.role === UserRole.Administrator, reason: '' };
  };

  const canDownload = (isOwner: boolean): IPermissionCheckResult => {
    if (isOwner) {
      return checkAll(
        [Plan.Admin, Plan.Training, Plan.Inactive],
        [WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Trainer, WorkspaceRole.Developer, WorkspaceRole.Collaborator],
        currentWorkspace,
        t
      );
    } else {
      return checkAll([Plan.Admin, Plan.Training, Plan.Inactive], [WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Trainer], currentWorkspace, t);
    }
  };

  return {
    canList,
    canReadCostList,
    canRun,
    canUpdate,
    canDelete,
    canImport,
    canDownload,
  };
};
